import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
  styled,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import SimpleModal from 'src/Component/SimpleModal'
import { useGetParkByIdQuery } from 'src/api/manageParksApi'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import InputField from 'src/Component/InputField'
import { Controller } from 'react-hook-form'
import FileInput from 'src/Component/FileInput'
import editIconRedBg from 'src/assets/icons/editIconRedBg.png'
import useEditNotification from './useEditNotification'
import SimpleDropdown from 'src/Component/SimpleDropdown'
import RadioField from '../RadioField'
import { notificationStatus, recipientsOptions, repeatEveryOptions } from 'src/utils/constants'

type EditNotificationModalProps = {
  id: number
  open: boolean
  onClose: () => void
  successCallback: () => void
  onDeleteClick: (id: number) => void
}

const EditNotificationModal = ({ id, open, onClose, successCallback, onDeleteClick }: EditNotificationModalProps) => {
  const {
    repeatEvery,
    control,
    errors,
    onSubmit,
    isLoadingEditNotification
  } = useEditNotification({
    successCallback,
    id,
  })

  const [requestEveryData, setRequestEveryData] = useState(repeatEvery);

  const handleRequestEveryField = (event: any, field: any) => {
    // console.log(event.target.value);
    setRequestEveryData(event.target.value);
    field.onChange(event);
  }

  useEffect(() => {
    setRequestEveryData(repeatEvery);
  }, [repeatEvery])

  return (
    <Box>
      <SimpleModal open={open} onClose={onClose}>
        <form onSubmit={onSubmit}>
          <Box>
            <Grid container mt={3}>
              <Grid xs={8} mb={2}>
                <Controller
                  name='title'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <InputField
                        name={field.name}
                        type='text'
                        label='Title'
                        placeholder='Enter the Title'
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.details}
                        required
                      />
                      {!!errors.title && (
                        <Grid container justifyContent='right'>
                          <Typography variant='subtitle2' color='red' textAlign='right'>
                            {errors.title.message}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>
            </Grid>

            <Grid container mt={3}>
              <Grid xs={8} mb={2}>
                <Controller
                  name='details'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <InputField
                        name={field.name}
                        type='text'
                        label='Details'
                        placeholder='Enter the Details'
                        multiline
                        rows={4}
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.details}
                        required
                      />
                      {!!errors.details && (
                        <Grid container justifyContent='right'>
                          <Typography variant='subtitle2' color='red' textAlign='right'>
                            {errors.details.message}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid xs={8} mb={2}>
                <Controller
                  name='status'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <SimpleDropdown
                      required
                        name={field.name}
                        value={field.value}
                        options={[
                          {
                            value: notificationStatus.ACTIVE,
                            label: 'Active',
                          },
                          {
                            value: notificationStatus.INACTIVE,
                            label: 'Inactive',
                          },
                        ]}
                        placeholder={'Select Status'}
                        label='Status'
                        onChange={(event: any) => handleRequestEveryField(event, field)}
                      />
                      {!!errors.status && (
                        <Grid container justifyContent='right'>
                          <Typography variant='subtitle2' color='red' textAlign='right'>
                            {errors.status.message}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>
            </Grid>

            <Grid container>
              <Grid xs={8} mb={2}>
                <Controller
                  name='scheduledTime'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <InputField
                        name={field.name}
                        type='time'
                        label='Time'
                        placeholder='Enter the Time'
                        value={field.value}
                        onChange={field.onChange}
                        error={!!errors.details}
                        required
                      />

                      {!!errors.scheduledTime && (
                        <Grid container justifyContent='right'>
                          <Typography variant='subtitle2' color='red' textAlign='right'>
                            {errors.scheduledTime.message}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>

              <Grid xs={4}>
                <Controller
                  name='repeatEvery'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <SimpleDropdown
                        name={field.name}
                        value={field.value}
                        options={repeatEveryOptions}
                        placeholder={'Select Interval'}
                        onChange={(event: any) => handleRequestEveryField(event, field)}
                      />

                      {!!errors.repeatEvery && (
                        <Grid container justifyContent='right'>
                          <Typography variant='subtitle2' color='red' textAlign='right'>
                            {errors.repeatEvery.message}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>
            </Grid>

            {
              requestEveryData === 'custom' &&
              <Grid container mt={3}>
                <Grid xs={8} mb={2}>
                  <Controller
                    name='customRepeatDays'
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <>
                        <InputField
                          name={field.name}
                          type='number'
                          label='Repeat days'
                          placeholder='Enter the repeat days'
                          value={field.value}
                          onChange={field.onChange}
                          error={!!errors.details}
                        />
                        {!!errors.customRepeatDays && (
                          <Grid container justifyContent='right'>
                            <Typography variant='subtitle2' color='red' textAlign='right'>
                              {errors.customRepeatDays.type === 'typeError'
                                ? 'Repeat Days should be number'
                                : errors.customRepeatDays.message}
                            </Typography>
                          </Grid>
                        )}
                      </>
                    )}
                  />
                </Grid>
              </Grid>
            }

            <Grid container>
              <Grid xs={8} mb={2}>
                <Controller
                  name='recipient'
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <>
                      <RadioField
                        name={field.name}
                        value={field.value}
                        options={recipientsOptions}
                        onChange={field.onChange}
                        label={'Recipients'}
                      />

                      {!!errors.recipient && (
                        <Grid container justifyContent='right'>
                        <Typography variant='subtitle2' color='red' textAlign='right'>
                          {errors.recipient.message}
                        </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                />
              </Grid>
            </Grid>

            <Button
              variant='contained'
              color='primary'
              type='submit'
              disabled={isLoadingEditNotification}
            >
              {isLoadingEditNotification ? <CircularProgress size={25} color='error' /> : 'SAVE'}
            </Button>
          </Box>
        </form>
      </SimpleModal>
    </Box>
  )
}

export default EditNotificationModal
